import React from 'react';
import { useApp } from '../context/AppContext';

export default function ProScreen({ onUpgrade }) {
  const { state, dispatch } = useApp();
  const lang = state?.language || 'en';

  const T = {
    en: { title: 'Unlock Premium', sub: 'Get the most out of Savely', price: '$14.9/month', cancel: 'Cancel anytime', cta: '👑 Get Premium →',
      features: [
        { icon: '📊', title: 'Weekly Savings Report', desc: 'Full spending analysis with charts & Savvy tips' },
        { icon: '🤖', title: 'AI Finance Coach', desc: 'Unlimited personal AI coach that knows your finances' },
        { icon: '🎯', title: 'Daily Challenges', desc: 'Personal saving challenges to build better habits' },
        { icon: '🎨', title: '3 Exclusive Themes', desc: 'Neon, Aurora & Obsidian premium themes' },
        { icon: '📦', title: 'Subscription Tracker', desc: 'Find and cut hidden subscriptions' },
        { icon: '🔮', title: 'Predictive Budget', desc: 'See when your budget runs out' },
        { icon: '🏆', title: '15 Achievements', desc: 'Unlock all prestigious achievements' },
      ]
    },
    no: { title: 'Lås opp Premium', sub: 'Få mest mulig ut av Savely', price: '149 kr/mnd', cancel: 'Avslutt når som helst', cta: '👑 Velg Premium →',
      features: [
        { icon: '📊', title: 'Ukentlig sparerapport', desc: 'Full utgiftsanalyse med grafer og Savvy-tips' },
        { icon: '🤖', title: 'AI Økonomicoach', desc: 'Ubegrenset personlig AI-coach som kjenner din økonomi' },
        { icon: '🎯', title: 'Daglige utfordringer', desc: 'Personlige spareutfordringer for bedre vaner' },
        { icon: '🎨', title: '3 eksklusive temaer', desc: 'Neon, Aurora og Obsidian premium-temaer' },
        { icon: '📦', title: 'Abonnement-tracker', desc: 'Finn og kutt skjulte abonnementer' },
        { icon: '🔮', title: 'Prediktivt budsjett', desc: 'Se når budsjettet ditt går tomt' },
        { icon: '🏆', title: '15 prestasjoner', desc: 'Lås opp alle prestisjefylte prestasjoner' },
      ]
    },
    sv: { title: 'Lås upp Premium', sub: 'Få ut mesta möjliga av Savely', price: '149 kr/mån', cancel: 'Avsluta när som helst', cta: '👑 Välj Premium →',
      features: [
        { icon: '📊', title: 'Veckorapport', desc: 'Full utgiftsanalys med grafer och Savvy-tips' },
        { icon: '🤖', title: 'AI Ekonomicoach', desc: 'Obegränsad personlig AI-coach som känner din ekonomi' },
        { icon: '🎯', title: 'Dagliga utmaningar', desc: 'Personliga sparutmaningar för bättre vanor' },
        { icon: '🎨', title: '3 exklusiva teman', desc: 'Neon, Aurora och Obsidian premium-teman' },
        { icon: '📦', title: 'Prenumerationstracker', desc: 'Hitta och klipp dolda prenumerationer' },
        { icon: '🔮', title: 'Prediktiv budget', desc: 'Se när din budget tar slut' },
        { icon: '🏆', title: '15 prestationer', desc: 'Lås upp alla prestigefyllda prestationer' },
      ]
    },
    da: { title: 'Lås op for Premium', sub: 'Få mest muligt ud af Savely', price: '149 kr/mdr', cancel: 'Annuller når som helst', cta: '👑 Vælg Premium →',
      features: [
        { icon: '📊', title: 'Ugentlig rapport', desc: 'Fuld udgiftsanalyse med grafer og Savvy-tips' },
        { icon: '🤖', title: 'AI Finanscoach', desc: 'Ubegrænset personlig AI-coach der kender din økonomi' },
        { icon: '🎯', title: 'Daglige udfordringer', desc: 'Personlige sparudfordringer for bedre vaner' },
        { icon: '🎨', title: '3 eksklusive temaer', desc: 'Neon, Aurora og Obsidian premium-temaer' },
        { icon: '📦', title: 'Abonnementstracker', desc: 'Find og klip skjulte abonnementer' },
        { icon: '🔮', title: 'Prediktivt budget', desc: 'Se hvornår dit budget løber tørt' },
        { icon: '🏆', title: '15 præstationer', desc: 'Lås op for alle præstigiøse præstationer' },
      ]
    },
  };

  const langKey = lang === 'sv' ? 'sv' : lang === 'da' ? 'da' : lang === 'no' ? 'no' : 'en';
  const t = T[langKey] || T.en;

  return (
    <div style={{ padding:'1.5rem 1rem 6rem', overflowY:'auto' }}>
      {/* Header */}
      <div style={{ textAlign:'center', marginBottom:28 }}>
        <div style={{ fontSize:48, marginBottom:8 }}>🚀</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:26, fontWeight:800, color:'var(--ink)' }}>
          {t.title}<span style={{ fontSize:14, verticalAlign:'super', marginLeft:4 }}>👑</span>
        </div>
        <div style={{ fontSize:13, color:'var(--ink3)', marginTop:6 }}>{t.sub}</div>
        <div style={{ marginTop:12, display:'inline-block', background:'linear-gradient(135deg,#7C3AED,#A855F7)', color:'#fff', borderRadius:999, padding:'6px 20px', fontFamily:'var(--font-display)', fontSize:18, fontWeight:800 }}>
          {t.price}
        </div>
      </div>

      {/* Features */}
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:24 }}>
        {t.features.map((f, i) => (
          <div key={i} style={{
            display:'flex', alignItems:'flex-start', gap:14,
            background: i < 2 ? 'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(168,85,247,0.04))' : 'var(--card)',
            border: i < 2 ? '1.5px solid rgba(124,58,237,0.25)' : '1px solid var(--border)',
            borderRadius:16, padding:'14px 16px',
          }}>
            <div style={{ fontSize:26, flexShrink:0 }}>{f.icon}</div>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:2, display:'flex', alignItems:'center', gap:6 }}>
                {f.title}
                {i < 2 && <span style={{ fontSize:9, background:'#7C3AED', color:'#fff', borderRadius:999, padding:'1px 6px', fontWeight:800 }}>TOP</span>}
              </div>
              <div style={{ fontSize:12, color:'var(--ink3)', lineHeight:1.4 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button onClick={() => { dispatch({ type:'SET_PLAN', payload:'premium' }); if(onUpgrade) onUpgrade(); }}
        style={{ width:'100%', padding:'16px', background:'linear-gradient(135deg,#7C3AED,#A855F7)', color:'#fff', border:'none', borderRadius:16, fontFamily:'var(--font-display)', fontSize:17, fontWeight:800, cursor:'pointer', boxShadow:'0 6px 24px rgba(124,58,237,0.4)', marginBottom:10 }}>
        {t.cta}
      </button>
      <div style={{ textAlign:'center', fontSize:12, color:'var(--ink3)' }}>{t.cancel}</div>
    </div>
  );
}
